import {put, call, all} from 'redux-saga/effects';
import ImportSource from "../../entities/importSource";
import * as Actions from "../actions/importSourceActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ImportSourceAction = {
    importSourceId: string;
    importSource: ImportSource;
    importSources: ImportSource[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneImportSource(action: ImportSourceAction) {
	const session = tabSession.captureGeneration();
	try {
		const importSource: ImportSource = yield call(api.findOne, 'importSource', action.importSourceId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.IMPORTSOURCE_FETCH_ONE_SUCCESS, importSourceId: action.importSourceId, importSource, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IMPORTSOURCE_FETCH_ONE_FAILURE, importSourceId: action.importSourceId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ImportSourceAction} action
 */
export function* saveImportSource(action: ImportSourceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ IMPORTSOURCE_SAVE saga', action.importSource?.id);

		if (action.importSource === undefined || action.importSource === null) {
			console.error('saveImportSource received a null');
			throw new Error('saveImportSource received a null');
		}

		if (!(action.importSource instanceof ImportSource)) {
			action.importSource = new ImportSource(action.importSource);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.importSource.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.importSource);
			const addResult = yield call(idb.add, 'importSource', action.importSource);
			if (addResult.isErr()) {
				console.error(`⮂ saveImportSource: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertImportSourceSave(action.importSource, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.IMPORTSOURCE_SAVE_FAILURE, importSource: action.importSource, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.IMPORTSOURCE_SAVE, importSource: action.importSource, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertImportSourceSave(action.importSource, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.IMPORTSOURCE_SAVE_FAILURE, importSource: action.importSource, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ImportSourceAction} action
 * @param {ImportSource[]} action.importSources
 */
export function* saveManyImportSources(action: ImportSourceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ IMPORTSOURCE_SAVE_MANY saga', action.importSources.map(importSource => importSource.id).join(','));

        if (!Array.isArray(action.importSources)) {
            console.error('saveManyImportSources expected an array, received', action.importSources);
            throw new Error('saveManyImportSources expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveImportSource), under these queue keys
		const dispatchedAddedOns: number[] = action.importSources.map(importSource => importSource?.addedOn);
		const revertAll = () => action.importSources.map((importSource, i) =>
			put(Actions.revertImportSourceSave(importSource, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: ImportSource[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.importSources.length; i++) {
				const importSource = action.importSources[i];
				importSource.persisted = false;
				ensureOperationId(importSource);
				toSave.push(importSource);
			}

			const addResult = yield call(idb.add, 'importSource', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyImportSources: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.IMPORTSOURCE_SAVE_MANY_FAILURE, importSources: action.importSources, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.IMPORTSOURCE_SAVE_MANY, importSources: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.IMPORTSOURCE_SAVE_MANY_FAILURE, importSources: action.importSources, status: 500, err});

			console.error(`Error saving ${action.importSources.length} importSources to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ImportSourceAction} action
 */
export function* removeImportSource(action: ImportSourceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.importSource === undefined || action.importSource === null || !(action.importSource instanceof ImportSource)) {
			console.error('removeImportSource expected type ImportSource, received', action.importSource);
			reportQueued(false);
			throw new Error('removeImportSource expected type ImportSource');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.importSource.status = 'D';
		action.importSource.addedOn = newHRTimestamp();
		action.importSource.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.importSource);
		const addResult = yield call(idb.add, 'importSource', action.importSource);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeImportSource: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.IMPORTSOURCE_REMOVE_FAILURE, importSource: action.importSource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTSOURCE_REMOVE, importSource: action.importSource});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IMPORTSOURCE_REMOVE_FAILURE, importSource: action.importSource, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this ImportSource.
	*
	* @param {ImportSourceAction} action
	*/
export function* restoreImportSource(action: ImportSourceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreImportSource expected getCurrentUserId() to return a value');
        return;
    }

	if (action.importSource === undefined || action.importSource === null) {
        console.error('restoreImportSource expected a ImportSource, received null');
        throw new Error('restoreImportSource expected a ImportSource, received null');
    }
    else if (!(action.importSource instanceof ImportSource)) action.importSource = new ImportSource(action.importSource); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.importSource.status = 'U';
		action.importSource.addedOn = newHRTimestamp();
		action.importSource.addedBy = userId;
		action.importSource.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.importSource);
		const addResult = yield call(idb.add, 'importSource', action.importSource);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreImportSource: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTSOURCE_RESTORE_FAILURE, importSource: action.importSource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTSOURCE_RESTORE, importSource: action.importSource});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.IMPORTSOURCE_RESTORE_FAILURE, importSource: action.importSource, status: 500, err});

		console.error('Error restoring from database', err);
	}
}